import { useState } from 'react';
import type { WebhookEndpoint, WebhookEventType } from '@salonomy/types';
import { Button, Field, Modal, Pill, TextInput, Toggle } from '@salonomy/ui';
import { useCreateWebhook, useUpdateWebhook } from '../../data/webhooks.js';
import { useToast } from '../../lib/toast.js';

/**
 * Create / edit a webhook endpoint (WS6 developer panel). The event picker lists the documented
 * event vocabulary (mirrors the API's WebhookEventType). On create the response carries the signing
 * secret ONCE — surfaced to the caller via `onSecret` so the panel can show "copy it now"; the list
 * never returns it again. Edits can rotate the secret (same one-time reveal).
 *
 * Owner/manager only — the parent panel is gated; no per-row escalation here.
 */

const EVENT_TYPES: Array<{ value: WebhookEventType; label: string }> = [
  { value: 'appointment.created', label: 'Appointment created' },
  { value: 'appointment.updated', label: 'Appointment updated' },
  { value: 'appointment.cancelled', label: 'Appointment cancelled' },
  { value: 'payment.succeeded', label: 'Payment succeeded' },
  { value: 'payment.refunded', label: 'Payment refunded' },
  { value: 'receipt.issued', label: 'Receipt issued' },
  { value: 'queue.ticket.joined', label: 'Queue ticket joined' },
  { value: 'queue.ticket.called', label: 'Queue ticket called' },
  { value: 'comms.message.sent', label: 'Comms message sent' },
  { value: 'stock.low', label: 'Stock low' },
];

export function WebhookModal({
  existing,
  onClose,
  onSecret,
}: {
  existing?: WebhookEndpoint;
  onClose: () => void;
  /** Called with the one-time signing secret after a create or a secret-rotation. */
  onSecret: (endpointId: string, secret: string) => void;
}) {
  const create = useCreateWebhook();
  const update = useUpdateWebhook();
  const toast = useToast();
  const editing = Boolean(existing);

  const [url, setUrl] = useState(existing?.url ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [events, setEvents] = useState<Set<WebhookEventType>>(
    new Set(existing?.eventTypes ?? ['appointment.created']),
  );
  const [rotateSecret, setRotateSecret] = useState(false);
  const [busy, setBusy] = useState(false);

  function toggleEvent(e: WebhookEventType) {
    setEvents((prev) => {
      const next = new Set(prev);
      if (next.has(e)) next.delete(e);
      else next.add(e);
      return next;
    });
  }

  async function submit() {
    const trimmed = url.trim();
    if (!/^https?:\/\//i.test(trimmed)) {
      toast.error('Enter a full URL starting with https://');
      return;
    }
    if (events.size === 0) {
      toast.error('Pick at least one event to send.');
      return;
    }
    setBusy(true);
    try {
      if (editing && existing) {
        const result = await update.mutateAsync({
          id: existing.id,
          body: {
            url: trimmed,
            eventTypes: [...events],
            description: description.trim() || null,
            ...(rotateSecret ? { rotateSecret: true } : {}),
          },
        });
        if ('secret' in result) onSecret(result.id, result.secret);
        toast.success('Webhook updated');
      } else {
        const created = await create.mutateAsync({
          url: trimmed,
          eventTypes: [...events],
          ...(description.trim() ? { description: description.trim() } : {}),
        });
        onSecret(created.id, created.secret);
        toast.success('Webhook created — copy the signing secret now');
      }
      onClose();
    } catch {
      toast.error(
        editing ? 'Could not update the webhook.' : 'Could not create the webhook — check the URL.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? 'Edit webhook' : 'Add webhook'}
      width={540}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy}>
            {editing ? 'Save' : 'Add webhook'}
          </Button>
        </>
      }
    >
      <Field label="Endpoint URL" hint="We POST a signed JSON payload here. Must be HTTPS.">
        <TextInput
          autoFocus
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://example.com/webhooks/salonomy"
        />
      </Field>

      <Field label="Description" hint="Optional — what this endpoint is for.">
        <TextInput
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g. Sync bookings to our CRM"
        />
      </Field>

      <div className="section-title" style={{ fontSize: 15, margin: '12px 0 4px' }}>
        Events
      </div>
      <div className="muted" style={{ fontSize: 12.5, marginBottom: 8 }}>
        Choose which events we deliver to this endpoint.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        {EVENT_TYPES.map((e) => (
          <label
            key={e.value}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 12.5,
              padding: '4px 2px',
            }}
          >
            <input
              type="checkbox"
              checked={events.has(e.value)}
              onChange={() => toggleEvent(e.value)}
            />
            <span>{e.label}</span>
            <span className="muted mono" style={{ fontSize: 11 }}>
              {e.value}
            </span>
          </label>
        ))}
      </div>

      {editing ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }}>
          <Toggle
            checked={rotateSecret}
            onChange={setRotateSecret}
            label="Rotate signing secret"
            labelHidden
          />
          <div style={{ fontSize: 13 }}>
            Rotate signing secret
            <div className="muted" style={{ fontSize: 11.5 }}>
              Generates a new secret and shows it once. The old secret stops verifying immediately.
            </div>
          </div>
        </div>
      ) : (
        <div className="muted" style={{ fontSize: 12, marginTop: 12 }}>
          We'll generate a signing secret and show it once after you add the endpoint — use it to
          verify our HMAC signature on every delivery.
        </div>
      )}

      {editing && existing ? (
        <div style={{ marginTop: 10 }}>
          <Pill tone={existing.active ? 'success' : 'danger'}>
            {existing.active ? 'Active' : 'Disabled'}
          </Pill>
        </div>
      ) : null}
    </Modal>
  );
}
